import { Eye } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { formatMoneyFull } from "@/lib/money";

interface AspCell {
  price: number;
  currency: string;
}

interface Props {
  externalId: string;
  displayName: string;
  fullName: string;
  asp: AspCell | null;
  // Omitted server-side when the user lacks sensitive.view_gross_margin,
  // so a null here also hides the Target GM line.
  cogs: number | null;
}

export default function ProductNamePopover({ externalId, displayName, fullName, asp, cogs }: Props) {
  const aspPrice = asp?.price ?? 0;
  const gm = aspPrice > 0 && cogs != null ? (aspPrice - cogs) / aspPrice : null;

  return (
    <Popover>
      <PopoverTrigger
        title={fullName}
        style={{
          fontSize: 12,
          fontWeight: 500,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as const,
          overflow: "hidden",
          lineHeight: "1.3",
        }}
        className="text-link text-left underline-offset-4 hover:underline cursor-pointer"
      >
        {/* Always-on "opens details" marker so the name reads as clickable
            without the mouse on it. */}
        <Eye
          aria-hidden
          className="inline-block align-middle -mt-0.5 mr-1 size-3 text-muted-foreground"
        />
        {displayName}
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm">
        <div>
          <span className="text-muted-foreground tabular-nums mr-1.5 select-all">{externalId}</span>
          <span className="font-semibold">{displayName}</span>
        </div>
        {fullName !== displayName && (
          <div className="mt-0.5 text-muted-foreground">{fullName}</div>
        )}
        <div className="mt-2 font-semibold tabular-nums">
          Target ASP:{" "}
          {asp ? formatMoneyFull(asp.price, asp.currency) : <span className="text-muted-foreground">—</span>}
        </div>
        {gm !== null && (
          <div className="font-semibold tabular-nums">Target GM: {(gm * 100).toFixed(1)}%</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
